import React, { useState, useEffect, useMemo } from 'react'
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts'
import Cookies from 'js-cookie'
import Topbar from '../../layouts/Topbar'
import Sidebar from '../../layouts/Sidebar'
import api from '../../../config/api'
import { Link } from 'react-router-dom'

const Shimmer = ({ width = '100%', height = '1rem', className = '', rounded = '4px' }) => (
    <span
        className={`placeholder placeholder-wave col-12 ${className}`}
        style={{ width, height, minHeight: height, borderRadius: rounded, display: 'block' }}
    />
)

const AnimatedCard = ({ children, index = 0, className = '' }) => (
    <div
        className={`stats-card-reveal ${className}`}
        style={{ animationDelay: `${index * 70}ms` }}
    >
        {children}
    </div>
)

const DashboardSkeleton = () => (
    <div className="page-wrapper">
        <div className="content pb-0">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="placeholder-glow">
                    <Shimmer width="220px" height="24px" className="mb-2" />
                    <Shimmer width="320px" height="14px" />
                </div>
                <div className="d-flex gap-2 placeholder-glow">
                    <Shimmer width="100px" height="32px" rounded="8px" />
                </div>
            </div>

            <div className="row">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="col-md-6 col-xl-4 d-flex" style={{ animationDelay: `${i * 80}ms` }}>
                        <div className="card flex-fill">
                            <div className="card-body placeholder-glow">
                                <Shimmer width="48px" height="48px" rounded="12px" className="mb-3" />
                                <Shimmer width="60%" height="14px" className="mb-2" />
                                <Shimmer width="40%" height="28px" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row mt-3">
                <div className="col-12 d-flex">
                    <div className="card flex-fill">
                        <div className="card-header placeholder-glow">
                            <Shimmer width="45%" height="18px" />
                        </div>
                        <div className="card-body placeholder-glow">
                            <Shimmer width="100%" height="300px" rounded="8px" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mt-3">
                <div className="card-header placeholder-glow">
                    <Shimmer width="40%" height="18px" />
                </div>
                <div className="card-body placeholder-glow">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Shimmer key={i} width="100%" height="40px" className="mb-2" rounded="6px" />
                    ))}
                </div>
            </div>
        </div>
    </div>
)

const RecentEvaluations = ({ reviews, loadingReviews, reviewsError }) => {
    return (
        <AnimatedCard index={4}>
            <div className="card shadow-sm border-0">
                <div className="card-header bg-transparent d-flex align-items-center justify-content-between py-3 border-bottom">
                    <div className="d-flex align-items-center gap-2">
                        <div
                            className="avatar avtar-sm d-flex align-items-center justify-content-center rounded-3"
                            style={{ backgroundColor: '#EEF2FF' }}
                        >
                            <i className="ti ti-clock-history fs-18" style={{ color: '#4F46E5' }} />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold fs-15">Recent Reviews</h5>
                            <p className="mb-0 fs-12 text-muted">Scored goals awaiting visibility</p>
                        </div>
                    </div>
                    <Link
                        to="/coo/my-goals-reviews"
                        className="btn btn-sm fw-semibold text-white shadow-sm"
                        style={{
                            backgroundColor: '#4F46E5',
                            borderRadius: '8px',
                            padding: '6px 18px',
                        }}
                    >
                        <span className="d-flex align-items-center gap-1">
                            <i className="ti ti-eye fs-14" />
                            View All
                        </span>
                    </Link>
                </div>

                <div className="card-body p-0">
                    {loadingReviews ? (
                        <div className="text-center text-muted py-5">
                            <div className="spinner-border spinner-border-sm text-primary mb-2" />
                            <p className="mb-0 fs-13">Loading reviews...</p>
                        </div>
                    ) : reviewsError ? (
                        <div className="alert alert-danger border-0 m-4 mb-0 fs-13 d-flex align-items-center gap-2">
                            <i className="ti ti-alert-triangle fs-18 flex-shrink-0" />
                            <span>{reviewsError}</span>
                        </div>
                    ) : !reviews || reviews.length === 0 ? (
                        <div className="text-center text-muted py-5 px-4">
                            <i className="ti ti-inbox fs-40 d-block mb-2 opacity-40" />
                            <p className="mb-0 fs-13">No recent reviews found.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover mb-0 align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="fs-13 fw-semibold text-uppercase text-muted py-3 px-4 border-0">
                                            Activity / KPI
                                        </th>
                                        <th className="fs-13 fw-semibold text-uppercase text-muted py-3 px-4 border-0">
                                            Performance Standard
                                        </th>
                                        <th className="fs-13 fw-semibold text-uppercase text-muted py-3 px-4 border-0">
                                            Metric
                                        </th>
                                        <th className="fs-13 fw-semibold text-uppercase text-muted py-3 px-4 border-0 text-center">
                                            Self Score
                                        </th>
                                        <th className="fs-13 fw-semibold text-uppercase text-muted py-3 px-4 border-0 text-center">
                                            HOD Score
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reviews.slice(0, 5).map((review, idx) => (
                                        <tr key={review.id || idx} className="border-bottom">
                                            <td className="py-3 px-4">
                                                <p className="mb-0 fw-medium fs-14 text-dark text-truncate" style={{ maxWidth: '260px' }}>
                                                    {review.activity || '—'}
                                                </p>
                                            </td>
                                            <td className="py-3 px-4">
                                                <p className="mb-0 fs-13 text-muted text-truncate" style={{ maxWidth: '320px' }}>
                                                    {review.performance_standard || '—'}
                                                </p>
                                            </td>
                                            <td className="py-3 px-4">
                                                <p className="mb-0 fs-13 text-muted text-truncate" style={{ maxWidth: '300px' }}>
                                                    {review.metric || '—'}
                                                </p>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span
                                                    className="badge fs-12 px-3 py-1 rounded-pill"
                                                    style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}
                                                >
                                                    {review.score != null ? review.score : '—'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span
                                                    className="badge fs-12 px-3 py-1 rounded-pill fw-semibold"
                                                    style={{ backgroundColor: '#DCFCE7', color: '#15803D' }}
                                                >
                                                    {review.hod_score != null ? review.hod_score : '—'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </AnimatedCard>
    )
}

const COSDashboard = () => {
    const [loading, setLoading] = useState(true)
    const [analytics, setAnalytics] = useState(null)
    const [profile, setProfile] = useState(null)
    const [profileLoading, setProfileLoading] = useState(true)

    const [loadingReviews, setLoadingReviews] = useState(true)
    const [reviewsError, setReviewsError] = useState(null)
    const [reviews, setReviews] = useState([])

    useEffect(() => {
        const fetchProfile = async () => {
            setProfileLoading(true)
            try {
                const response = await api.get('/api/users/me')
                setProfile(response.data)
            } catch {
                setProfile(null)
            } finally {
                setProfileLoading(false)
            }
        }
        fetchProfile()
    }, [])

    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!profile?.employee?.id) return
            setLoading(true)
            try {
                const token = Cookies.get('token')
                const response = await api.get('/api/performance-analytics', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { employee: profile.employee.id },
                })
                setAnalytics(response.data)
            } catch {
                setAnalytics(null)
            } finally {
                setLoading(false)
            }
        }
        if (profile && !profileLoading) {
            fetchAnalytics()
        }
    }, [profile, profileLoading])

    useEffect(() => {
        const fetchReviews = async () => {
            setLoadingReviews(true)
            setReviewsError(null)
            try {
                const token = Cookies.get('token')
                const response = await api.get('/api/users/me/', {
                    headers: { Authorization: `Bearer ${token}` },
                })
                const completed = (Array.isArray(response.data.employee.goals) ? response.data.employee.goals : [])
                    .filter((g) => (g.status === 'complete' || g.status === 'completed') && Number(g.hod_score) > 0)
                completed.sort(
                    (a, b) =>
                        new Date(b.updated_at || b.completed_at || b.created_at) -
                        new Date(a.updated_at || a.completed_at || a.created_at)
                )
                setReviews(completed)
            } catch {
                setReviewsError('Failed to fetch recent reviews.')
            } finally {
                setLoadingReviews(false)
            }
        }
        fetchReviews()
    }, [])

    const stats = analytics?.overall_statistics || {}
    const timeliness = analytics?.overall_statistics || {}

    const quarterlyTrendsData = useMemo(() => {
        if (!analytics?.quarterly_trends) return []
        return Object.entries(analytics.quarterly_trends).map(([quarter, qStats]) => ({
            quarter,
            ...qStats,
        }))
    }, [analytics])

    const animStyles = `
        @keyframes statsFadeInUp {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .stats-card-reveal {
            opacity: 0;
            animation: statsFadeInUp 0.45s ease forwards;
        }
    `

    if (loading && !analytics) {
        return (
            <>
                <style>{animStyles}</style>
                <div
                    data-sidebar-skin="dark"
                    data-header-skin="light"
                    data-navbar-brand-skin="dark"
                    data-sidebar-state="expand"
                >
                    <Topbar />
                    <Sidebar />
                    <DashboardSkeleton />
                </div>
            </>
        )
    }

    return (
        <>
            <style>{animStyles}</style>
            <div
                data-sidebar-skin="dark"
                data-header-skin="light"
                data-navbar-brand-skin="dark"
                data-sidebar-state="expand"
            >
                <Topbar />
                <Sidebar />
                <div className="page-wrapper">
                    <div className="content pb-0">
                        <AnimatedCard index={0} className="mb-4">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                <div>
                                    <h4 className="fw-bold mb-1">Performance Dashboard</h4>
                                    <p className="text-muted mb-0 fs-13">Overview of my performance.</p>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <div
                                        className="avatar avtar-sm bg-light-primary rounded-2 d-flex align-items-center justify-content-center"
                                        style={{ backgroundColor: '#EEF2FF' }}
                                    >
                                        <i className="ti ti-calendar-month fs-16" style={{ color: '#4F46E5' }} />
                                    </div>
                                    <span className="text-muted fs-13">
                                        {new Date().toLocaleDateString(undefined, {
                                            month: 'long',
                                            year: 'numeric',
                                        })}
                                    </span>
                                </div>
                            </div>
                        </AnimatedCard>

                        <div className="row g-3 mb-3">
                            <AnimatedCard index={1} className="col-12 col-md-6 col-xl-4 d-flex">
                                <div className="card flex-fill border-0 bg-light">
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-start justify-content-between mb-3">
                                            <div className="avatar avtar-sm bg-indigo mb-2 d-flex align-items-center justify-content-center rounded-3"
                                                style={{ backgroundColor: '#4F46E5' }}>
                                                <i className="ti ti-target fs-20 text-white" />
                                            </div>
                                            <span className="badge fs-11 rounded-pill" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}>
                                                Goals
                                            </span>
                                        </div>
                                        <h6 className="fs-13 fw-semibold mb-1 text-muted">Completed Goals</h6>
                                        <h4 className="fw-bold mb-2 text-dark">
                                            {stats.completed_goals ?? 0}
                                            <small className="fs-14 text-muted ms-1 fw-normal">
                                                / {stats.total_goals ?? 0}
                                            </small>
                                        </h4>
                                        <div className="progress" style={{ height: '6px', backgroundColor: '#e0e7ff' }}>
                                            <div
                                                className="progress-bar rounded-3"
                                                role="progressbar"
                                                style={{
                                                    width: `${
                                                        stats.total_goals
                                                            ? Math.round(
                                                                  (stats.completed_goals / stats.total_goals) * 100
                                                              )
                                                            : 0
                                                    }%`,
                                                    background: 'linear-gradient(90deg, #4F46E5, #6366F1)',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </AnimatedCard>

                            <AnimatedCard index={2} className="col-12 col-md-6 col-xl-4 d-flex">
                                <div className="card flex-fill border-0 bg-light">
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-start justify-content-between mb-3">
                                            <div className="avatar avtar-sm bg-teal mb-2 d-flex align-items-center justify-content-center rounded-3"
                                                style={{ backgroundColor: '#0D9488' }}>
                                                <i className="ti ti-trending-up fs-20 text-white" />
                                            </div>
                                            <span className="badge fs-11 rounded-pill bg-teal-subtle text-teal"
                                                style={{ backgroundColor: '#CCFBF1', color: '#0F766E' }}>
                                                Score
                                            </span>
                                        </div>
                                        <h6 className="fs-13 fw-semibold mb-1 text-muted">Avg Performance</h6>
                                        <h4 className="fw-bold mb-2 text-dark">
                                            {stats.average_progress
                                                ? `${stats.average_progress}%`
                                                : '0%'}
                                        </h4>
                                        <div className="d-flex align-items-center gap-1 fs-12 text-success fw-medium">
                                            <i className="ti ti-arrow-up-right fs-14" />
                                            Progress trend
                                        </div>
                                    </div>
                                </div>
                            </AnimatedCard>

                            <AnimatedCard index={3} className="col-12 col-xl-4 d-flex">
                                <div className="card flex-fill border-0 bg-light">
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-start justify-content-between mb-3">
                                            <div className="avatar avtar-sm bg-purple mb-2 d-flex align-items-center justify-content-center rounded-3"
                                                style={{ backgroundColor: '#7C3AED' }}>
                                                <i className="ti ti-clock-hour-5 fs-20 text-white" />
                                            </div>
                                            <span className="badge fs-11 rounded-pill"
                                                style={{ backgroundColor: '#F3E8FF', color: '#7C3AED' }}>
                                                Timeliness
                                            </span>
                                        </div>
                                        <h6 className="fs-13 fw-semibold mb-2 text-muted">
                                            Goal Completion Timeliness
                                        </h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            <span
                                                className="badge fs-12 px-3 py-1 rounded-pill text-white fw-semibold"
                                                style={{ backgroundColor: '#22c55e' }}
                                            >
                                                On Time: {timeliness.on_time_rate != null ? timeliness.on_time_rate : 0}%
                                            </span>
                                            <span
                                                className="badge fs-12 px-3 py-1 rounded-pill text-white fw-semibold"
                                                style={{ backgroundColor: '#3b82f6' }}
                                            >
                                                Early: {timeliness.early_rate != null ? timeliness.early_rate : 0}%
                                            </span>
                                            <span
                                                className="badge fs-12 px-3 py-1 rounded-pill text-white fw-semibold"
                                                style={{ backgroundColor: '#ef4444' }}
                                            >
                                                Late: {timeliness.late_rate != null ? timeliness.late_rate : 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </AnimatedCard>
                        </div>

                        <div className="row g-3 mb-3">
                            <AnimatedCard index={4} className="col-12 d-flex">
                                <div className="card flex-fill border-0 shadow-sm">
                                    <div className="card-header bg-transparent d-flex align-items-center justify-content-between py-3 border-bottom">
                                        <div className="d-flex align-items-center gap-2">
                                            <div
                                                className="avatar avtar-sm rounded-3 d-flex align-items-center justify-content-center"
                                                style={{ backgroundColor: '#EEF2FF' }}
                                            >
                                                <i className="ti ti-chart-area-line fs-18" style={{ color: '#4F46E5' }} />
                                            </div>
                                            <div>
                                                <h5 className="mb-0 fw-bold fs-15">Quarterly Performance Trends</h5>
                                                <p className="mb-0 fs-12 text-muted">Completion rate over financial quarters</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body p-4">
                                        <div style={{ width: '100%', height: '300px' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart
                                                    data={quarterlyTrendsData}
                                                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                                                >
                                                    <defs>
                                                        <linearGradient id="colorQuarterly" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.35} />
                                                            <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" vertical={false} />
                                                    <XAxis
                                                        dataKey="quarter"
                                                        tick={{ fontSize: 12, fill: '#6b7280' }}
                                                        axisLine={{ stroke: '#e5e7eb' }}
                                                        tickLine={false}
                                                    />
                                                    <YAxis
                                                        domain={[0, 100]}
                                                        tickFormatter={(v) => `${v}%`}
                                                        tick={{ fontSize: 12, fill: '#6b7280' }}
                                                        axisLine={false}
                                                        tickLine={false}
                                                    />
                                                    <Tooltip
                                                        formatter={(value) => [`${value}%`, 'Completion Rate']}
                                                        contentStyle={{
                                                            borderRadius: '8px',
                                                            border: 'none',
                                                            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                                                            padding: '10px 14px',
                                                        }}
                                                        labelStyle={{ fontWeight: 600, color: '#111827', marginBottom: '4px' }}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="completion_rate"
                                                        stroke="#4F46E5"
                                                        strokeWidth={3}
                                                        fillOpacity={1}
                                                        fill="url(#colorQuarterly)"
                                                        dot={{
                                                            r: 5,
                                                            fill: '#4F46E5',
                                                            stroke: '#fff',
                                                            strokeWidth: 2,
                                                        }}
                                                        activeDot={{ r: 7, stroke: '#fff', strokeWidth: 2 }}
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>
                            </AnimatedCard>
                        </div>

                        <RecentEvaluations
                            reviews={reviews}
                            loadingReviews={loadingReviews}
                            reviewsError={reviewsError}
                        />
                    </div>

                    <div
                        className="footer d-flex align-items-center justify-content-between border-top px-4 py-3 flex-wrap gap-2"
                        style={{ backgroundColor: '#fff' }}
                    >
                        <p className="mb-0 fs-12 text-muted">
                            © 2026 © Casoe Technologies. All rights reserved.
                        </p>
                        <p className="mb-0 fs-12 text-muted">
                            Designed &amp; Developed by{' '}
                            <strong style={{ color: '#4F46E5' }}>Casoe Technologies</strong>
                        </p>
                    </div>
                </div>
            </div>
        </>
    )
}

export default COSDashboard
